import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUser, FiCreditCard, FiPhone, FiAlertCircle, FiArrowRight } from 'react-icons/fi';
import { styles } from '../../styles';
import { validateCPF, formatCPF, validatePhone, formatPhone } from '../../../../validators';
import { verifyCpfExist } from '../../../../Api';
import { notify } from '../../../../components/Notify';

export default function Step1Info({ next, update, data }) {
    const navigate = useNavigate();
    const [errors, setErrors] = useState({});
    const [checking, setChecking] = useState(false);

    const setError = (key, msg) => setErrors(prev => ({ ...prev, [key]: msg }));
    const clearError = (key) => setErrors(prev => ({ ...prev, [key]: undefined }));

    const handleCpfChange = (e) => {
        const formatted = formatCPF(e.target.value);
        update({ cpf: formatted });
        if (errors.cpf) clearError('cpf');
    };

    const handlePhoneChange = (e) => {
        const formatted = formatPhone(e.target.value);
        update({ telefone: formatted });
        if (errors.telefone) clearError('telefone');
    };

    const validateAll = () => {
        const next = {};
        if (!data.nomeCompleto || data.nomeCompleto.trim().length < 3) {
            next.nomeCompleto = 'Informe o nome completo.';
        } else if (!data.nomeCompleto.trim().includes(' ')) {
            next.nomeCompleto = 'Informe nome e sobrenome.';
        }

        const cpfRes = validateCPF(data.cpf);
        if (!cpfRes.valid) next.cpf = cpfRes.error;

        const phoneRes = validatePhone(data.telefone);
        if (!phoneRes.valid) next.telefone = phoneRes.error;

        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleNext = async () => {
        if (!validateAll()) return;
        if (checking) return;

        // Pre-check: se o CPF já existe, o cadastro vai falhar lá no fim.
        // Antecipa pra redirecionar pro login agora (UX) em vez de só
        // descobrir depois de preencher 3 telas.
        setChecking(true);
        try {
            const cpfLimpo = (data.cpf || '').replace(/\D/g, '');
            const res = await verifyCpfExist({ cpf: cpfLimpo });
            if (res.data?.find === true) {
                notify('CPF já cadastrado. Redirecionando para o login…', 'warning');
                setTimeout(() => {
                    navigate('/login');
                }, 1200);
                return;
            }
        } catch (err) {
            // Falha de rede no pre-check não deve travar o cadastro — o backend
            // ainda valida na criação. Apenas loga e segue.
            console.warn('Falha ao verificar CPF:', err);
        } finally {
            setChecking(false);
        }

        next();
    };

    return (
        <div>
            {/* Nome */}
            <div style={styles.inputGroup}>
                <label style={styles.label} htmlFor="nomeCompleto">Nome completo</label>
                <div style={styles.inputWrapper}>
                    <span style={styles.inputIcon}><FiUser size={18} /></span>
                    <input
                        id="nomeCompleto"
                        type="text"
                        placeholder="Ex: João da Silva"
                        autoComplete="name"
                        value={data.nomeCompleto || ''}
                        onChange={(e) => { update({ nomeCompleto: e.target.value }); if (errors.nomeCompleto) clearError('nomeCompleto'); }}
                        onFocus={(e) => e.target.style.borderColor = '#10B981'}
                        onBlur={(e) => {
                            e.target.style.borderColor = errors.nomeCompleto ? '#F43F5E' : '#E2E8F0';
                            if (data.nomeCompleto && data.nomeCompleto.trim().length < 3) {
                                setError('nomeCompleto', 'Informe o nome completo.');
                            }
                        }}
                        style={styles.input(!!errors.nomeCompleto)}
                    />
                </div>
                {errors.nomeCompleto && (
                    <div style={styles.fieldError}><FiAlertCircle size={14} /> {errors.nomeCompleto}</div>
                )}
            </div>

            {/* CPF */}
            <div style={styles.inputGroup}>
                <label style={styles.label} htmlFor="cpf">CPF</label>
                <div style={styles.inputWrapper}>
                    <span style={styles.inputIcon}><FiCreditCard size={18} /></span>
                    <input
                        id="cpf"
                        type="text"
                        inputMode="numeric"
                        placeholder="000.000.000-00"
                        value={data.cpf || ''}
                        onChange={handleCpfChange}
                        onFocus={(e) => e.target.style.borderColor = '#10B981'}
                        onBlur={(e) => {
                            e.target.style.borderColor = errors.cpf ? '#F43F5E' : '#E2E8F0';
                            if (data.cpf) {
                                const r = validateCPF(data.cpf);
                                setError('cpf', r.valid ? undefined : r.error);
                            }
                        }}
                        style={styles.input(!!errors.cpf)}
                    />
                </div>
                {errors.cpf && (
                    <div style={styles.fieldError}><FiAlertCircle size={14} /> {errors.cpf}</div>
                )}
            </div>

            {/* Telefone */}
            <div style={styles.inputGroup}>
                <label style={styles.label} htmlFor="telefone">Telefone / WhatsApp</label>
                <div style={styles.inputWrapper}>
                    <span style={styles.inputIcon}><FiPhone size={18} /></span>
                    <input
                        id="telefone"
                        type="tel"
                        inputMode="tel"
                        placeholder="(00) 00000-0000"
                        autoComplete="tel"
                        value={data.telefone || ''}
                        onChange={handlePhoneChange}
                        onFocus={(e) => e.target.style.borderColor = '#10B981'}
                        onBlur={(e) => {
                            e.target.style.borderColor = errors.telefone ? '#F43F5E' : '#E2E8F0';
                            if (data.telefone) {
                                const r = validatePhone(data.telefone);
                                setError('telefone', r.valid ? undefined : r.error);
                            }
                        }}
                        style={styles.input(!!errors.telefone)}
                    />
                </div>
                {errors.telefone && (
                    <div style={styles.fieldError}><FiAlertCircle size={14} /> {errors.telefone}</div>
                )}
            </div>

            <button
                onClick={handleNext}
                disabled={checking}
                style={{
                    ...styles.signUpButton,
                    ...(checking ? { opacity: 0.7, cursor: 'not-allowed' } : {}),
                }}
                onMouseEnter={(e) => !checking && (e.currentTarget.style.transform = 'translateY(-1px)')}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
                {checking ? 'Verificando…' : <>Próximo <FiArrowRight size={18} /></>}
            </button>
        </div>
    );
}
